import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, FormControlLabel, MenuItem } from '@mui/material';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CloseIcon from '@mui/icons-material/Close';
import moment from 'moment-timezone';
import Button from '../../../../../components/Button';
import Info from '../../../../../components/Info';
import { MuiTextField, MuiDatePicker } from '../../../../../components/MuiComponents/FormInputs';
import { useGenptScheduleValidateName } from '../../../../../hooks/siembagpt';
import { buildGenptScheduleTimeConflictKey } from '../../utils/mapGenptScheduleToEditPrefill';
import { hasGenptScheduleSlotConflict } from '../../utils/genptScheduleTimeConflictCheck';
import { getFreezingWallMsRangesFromUi } from '../utils/buildGenptSchedulePayload';

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) =>
  String(i + 1).padStart(2, '0')
);
const MINUTE_OPTIONS = ['00', '15', '30', '45'];

const TIMEZONE_OPTIONS = [
  { value: 'America/Los_Angeles', label: 'America/Los Angeles - UTC-8h' },
  { value: 'America/New_York', label: 'America/New York - UTC-5h' },
  { value: 'America/Chicago', label: 'America/Chicago - UTC-6h' },
  { value: 'America/Denver', label: 'America/Denver - UTC-7h' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'Europe/London - UTC+0h' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin - UTC+1h' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata - UTC+5:30h' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo - UTC+9h' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney - UTC+11h' },
];

const REPEAT_UNIT_OPTIONS = ['Day', 'Week', 'Month'];

/** ROAR-4325 (exact ticket copy) */
const SINGLE_EXECUTION_END_WARNING =
  'Warning: The selected end date occurs before the next scheduled repetition. The schedule will only run once and will not repeat.';

/** ROAR-4385: zero effective runs when freeze windows defer every occurrence past “Ends on”. */
const FREEZE_ZERO_EXECUTIONS_ERROR =
  "Error: Zero executions detected. Please adjust your freeze periods or extend the 'Ends On' date to allow the schedule to run.";

/** ROAR-4386 */
const FREEZING_PERIODS_OVERLAP_ERROR =
  'Freezing periods cannot overlap. Adjust dates or times so no two periods overlap.';

/** ROAR-4323: “End after” execution count — max 3 digits, value cap 999 */
const ENDS_AFTER_MAX_EXECUTIONS = 999;

/** ROAR-4360: debounce before POST `/genpt/schedules/validate` with `{ check: "name", name }` */
const SCHEDULE_NAME_VALIDATE_DEBOUNCE_MS = 450;

/** ROAR-4341: debounce POST `/genpt/schedules/validate` (check=slot) by assetId — server mirrors UTC instant + recurrence conflict rules */
const SCHEDULE_TIME_CONFLICT_DEBOUNCE_MS = 500;

function clampEndsAfterCountInitial(raw) {
  const fallback = '10';
  if (raw == null || String(raw).trim() === '') return fallback;
  const n = parseInt(String(raw), 10);
  if (Number.isNaN(n)) return fallback;
  return String(Math.min(ENDS_AFTER_MAX_EXECUTIONS, Math.max(1, n)));
}

const DAYS_OF_WEEK = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];

const MONTHLY_DATE_OPTIONS = Array.from({ length: 31 }, (_, i) => {
  const d = i + 1;
  let suffix = 'th';
  if (d === 1 || d === 21 || d === 31) suffix = 'st';
  else if (d === 2 || d === 22) suffix = 'nd';
  else if (d === 3 || d === 23) suffix = 'rd';
  return `${d}${suffix}`;
});

/** Long select lists (monthly day 1–31, timezone): capped height + scroll per ROAR-4314. */
const SELECT_MENU_SCROLL = {
  MenuProps: {
    PaperProps: { sx: { maxHeight: 220 } },
    anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
    transformOrigin: { vertical: 'top', horizontal: 'left' },
  },
};

const getRepeatLimits = (unit) => {
  switch (unit) {
    case 'Day': return { min: 1, max: 31 };
    case 'Week': return { min: 1, max: 12 };
    case 'Month': return { min: 1, max: 12 };
    default: return { min: 1, max: 31 };
  }
};

const resolveRelativeDay = (year, month, position, dayType) => {
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const candidates = [];
  const daysMap = {
    Sunday: 0, Monday: 1, Tuesday: 2, Wednesday: 3,
    Thursday: 4, Friday: 5, Saturday: 6,
  };

  for (let i = 1; i <= daysInMonth; i++) {
    const d = new Date(year, month, i);
    const dow = d.getDay();

    if (dayType === 'Day') {
      candidates.push(d);
    } else if (dayType === 'Weekday' && dow >= 1 && dow <= 5) {
      candidates.push(d);
    } else if (dayType === 'Weekend' && (dow === 0 || dow === 6)) {
      candidates.push(d);
    } else if (daysMap[dayType] === dow) {
      candidates.push(d);
    }
  }

  if (candidates.length === 0) return null;

  if (dayType === 'Weekend') {
    if (position === 'First') {
      return candidates.find((d) => d.getDay() === 6) || candidates[0];
    }
    if (position === 'Last') {
      const saturdays = candidates.filter((d) => d.getDay() === 6);
      return saturdays.length > 0
        ? saturdays[saturdays.length - 1]
        : candidates[candidates.length - 1];
    }
  }

  if (position === 'Last') return candidates[candidates.length - 1];

  const posMap = { First: 0, Second: 1, Third: 2, Fourth: 3, Fifth: 4 };
  if (posMap[position] !== undefined && candidates[posMap[position]]) {
    return candidates[posMap[position]];
  }

  return candidates[0];
};

const formatDateTime = (dateObj) => {
  if (!dateObj || isNaN(dateObj.getTime())) return '-';
  const day = dateObj.getDate().toString().padStart(2, '0');
  const month = dateObj.toLocaleString('default', { month: 'long' });
  const year = dateObj.getFullYear();
  let hours = dateObj.getHours();
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${day} ${month}, ${year} - ${hours}:${minutes} ${ampm}`;
};

const DAY_ABB_TO_JS_WEEKDAY = {
  SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6,
};

/**
 * ROAR-4367 / ROAR-4368: Weekly “on specific days” with repeat every 1 week runs on each selected weekday
 * at the chosen wall time — not “same weekday + 7 days”. Used for execution preview, zero-run validation,
 * and correct “Total Number Of Executions” when several selected days fall before “Ends on”.
 */
function nextWeeklySpecificSlotOnOrAfter(notBefore, selectedDayAbbrevs, hr24, minVal) {
  if (!notBefore || !selectedDayAbbrevs?.length) return null;
  const nums = selectedDayAbbrevs
    .map((d) => DAY_ABB_TO_JS_WEEKDAY[d])
    .filter((n) => n != null);
  if (!nums.length) return null;
  const startCal = new Date(
    notBefore.getFullYear(),
    notBefore.getMonth(),
    notBefore.getDate(),
    0,
    0,
    0,
    0
  );
  for (let offset = 0; offset < 370; offset += 1) {
    const cal = new Date(startCal);
    cal.setDate(startCal.getDate() + offset);
    if (!nums.includes(cal.getDay())) continue;
    const slot = new Date(
      cal.getFullYear(),
      cal.getMonth(),
      cal.getDate(),
      hr24,
      minVal,
      0,
      0
    );
    if (slot >= notBefore) return slot;
  }
  return null;
}

function nextWeeklySpecificSlotAfter(prevSlot, selectedDayAbbrevs, hr24, minVal) {
  if (!prevSlot) return null;
  const strictlyAfter = new Date(prevSlot.getTime() + 1);
  return nextWeeklySpecificSlotOnOrAfter(strictlyAfter, selectedDayAbbrevs, hr24, minVal);
}

/** Advance wall time past any freezing ranges that contain `ms` (inclusive), matching deferred-run semantics. */
function resolveMsThroughFreezingRanges(ms, ranges) {
  let t = ms;
  for (let guard = 0; guard < 64; guard += 1) {
    let moved = false;
    for (let i = 0; i < ranges.length; i += 1) {
      const r = ranges[i];
      if (t >= r.start && t <= r.end) {
        t = r.end + 1;
        moved = true;
        break;
      }
    }
    if (!moved) break;
  }
  return t;
}

/** Align stored HH:MM with select options so MUI Select opens and shows a value (e.g. freezing periods). */
const normalizeTimeSelectParts = (timeStr) => {
  const [h = '09', m = '00'] = String(timeStr || '09:00').split(':');
  const hNum = parseInt(h, 10);
  const hourNum = Number.isNaN(hNum) ? 9 : Math.min(12, Math.max(1, hNum));
  const hour = String(hourNum).padStart(2, '0');
  const minPadded = String(m).padStart(2, '0');
  const min = MINUTE_OPTIONS.includes(minPadded) ? minPadded : '00';
  const hourFinal = HOUR_OPTIONS.includes(hour) ? hour : '09';
  return { hour: hourFinal, min };
};

const GENPT_SCHEDULE_ERROR_ICON_SRC = '/images/genpt/failed_test.svg';

function ScheduleSectionErrorIcon({ className = '' }) {
  return (
    <img
      src={GENPT_SCHEDULE_ERROR_ICON_SRC}
      alt=""
      width={20}
      height={20}
      className={`shrink-0 object-contain ${className}`.trim()}
      aria-hidden
    />
  );
}

/** Pairwise overlap on inclusive wall-clock ranges (same semantics as `getFreezingWallMsRangesFromUi`). */
function freezingPeriodRangesOverlapInclusive(ranges) {
  if (!Array.isArray(ranges) || ranges.length < 2) return false;
  for (let i = 0; i < ranges.length; i += 1) {
    for (let j = i + 1; j < ranges.length; j += 1) {
      const a = ranges[i];
      const b = ranges[j];
      if (a.start <= b.end && b.start <= a.end) return true;
    }
  }
  return false;
}

/**
 * Inclusive calendar-day overlap on `YYYY-MM-DD` (lexicographic order matches chronological).
 * Each period is treated as covering every calendar day from startDate through endDate so changing
 * only dates re-validates without relying on wall-clock parsing alone (schedule modal / QA).
 */
function freezingPeriodCalendarDayRangesOverlapInclusive(periods) {
  if (!Array.isArray(periods) || periods.length < 2) return false;
  const ranges = periods
    .map((p) => {
      const sd = String(p?.startDate ?? '').trim();
      const ed = String(p?.endDate ?? '').trim();
      if (!sd || !ed) return null;
      const s = sd <= ed ? sd : ed;
      const e = sd <= ed ? ed : sd;
      return { s, e };
    })
    .filter(Boolean);
  if (ranges.length < 2) return false;
  for (let i = 0; i < ranges.length; i += 1) {
    for (let j = i + 1; j < ranges.length; j += 1) {
      const a = ranges[i];
      const b = ranges[j];
      if (a.s <= b.e && b.s <= a.e) return true;
    }
  }
  return false;
}

function freezingPeriodsOverlapFromUi(periods) {
  if (freezingPeriodCalendarDayRangesOverlapInclusive(periods)) return true;
  const wall = getFreezingWallMsRangesFromUi(periods);
  return freezingPeriodRangesOverlapInclusive(wall);
}

/**
 * Schedule - Reusable component for test scheduling configuration.
 *
 * Props:
 *   initialValues        - schedule data for restoring state
 *   isReadOnly           - render in display mode
 *   isDisabled           - disable all interactions
 *   onSubmit             - callback with schedule data object
 *   submitButtonLabel    - primary action label (e.g. Schedule GenPT modal vs chat)
 *   assetId              - when set, debounced check vs other schedules on same asset (ROAR-4341)
 *   excludeScheduleId    - when editing, omit this schedule id from conflict list
 *   lockStartDate        - when true (e.g. editing an existing schedule), start date stays prefilled but cannot be changed
 */
function ScheduleTest({
  initialValues = {},
  isReadOnly = false,
  isDisabled = false,
  onSubmit = () => {},
  submitButtonLabel = 'SCHEDULE TEST',
  assetId = null,
  excludeScheduleId = null,
  lockStartDate = false,
}) {
  console.log('-=- initialValues = ', initialValues);
  const [scheduleName, setScheduleName] = useState(
    initialValues.scheduleName || ''
  );
  const [timeZone, setTimeZone] = useState(
    initialValues.timeZone || 'America/Los_Angeles'
  );

  const [startDate, setStartDate] = useState(
    initialValues.startDate != null && initialValues.startDate !== ''
      ? initialValues.startDate
      : ''
  );
  const [startHour, setStartHour] = useState(
    initialValues.startHour || '09'
  );
  const [startMin, setStartMin] = useState(
    initialValues.startMin || '00'
  );
  const [startAmPm, setStartAmPm] = useState(
    initialValues.startAmPm || 'AM'
  );

  const [recurrencyType, setRecurrencyType] = useState(
    initialValues.recurrencyType || 'custom'
  );

  const [repeatValue, setRepeatValue] = useState(
    initialValues.repeatValue || '1'
  );
  const [repeatUnit, setRepeatUnit] = useState(
    initialValues.repeatUnit || 'Week'
  );

  const [weeklyPattern, setWeeklyPattern] = useState(
    initialValues.weeklyPattern || 'specific'
  );
  const [selectedDays, setSelectedDays] = useState(() => {
    const d = initialValues?.selectedDays;
    return Array.isArray(d) && d.length > 0 ? [...d] : ['MON'];
  });

  const [monthlyPattern, setMonthlyPattern] = useState(
    initialValues.monthlyPattern || 'specific_date'
  );
  const [monthlySpecificDate, setMonthlySpecificDate] = useState(
    initialValues.monthlySpecificDate || '29th'
  );
  const [monthlyRelativePosition, setMonthlyRelativePosition] = useState(
    initialValues.monthlyRelativePosition || 'First'
  );
  const [monthlyRelativeDay, setMonthlyRelativeDay] = useState(
    initialValues.monthlyRelativeDay || 'Weekend'
  );

  const [endsType, setEndsType] = useState(
    initialValues.endsType || 'never'
  );
  const [endsOnDate, setEndsOnDate] = useState(
    initialValues.endsOnDate || '2026-05-30'
  );
  const [endsAfterCount, setEndsAfterCount] = useState(() =>
    clampEndsAfterCountInitial(initialValues.endsAfterCount)
  );

  const [isFreezingEnabled, setIsFreezingEnabled] = useState(
    initialValues.isFreezingEnabled || false
  );
  const [freezingPeriods, setFreezingPeriods] = useState(
    initialValues.freezingPeriods || [
      {
        id: 1,
        startDate: '',
        startTime: '09:00',
        startAmPm: 'AM',
        endDate: '',
        endTime: '05:00',
        endAmPm: 'PM',
      },
    ]
  );

  const [debouncedTrimmedScheduleName, setDebouncedTrimmedScheduleName] = useState('');
  /**
   * ROAR-4363: GET /schedules can restore multiple `days_of_week`; the start-date effect below must not
   * collapse them to a single weekday derived from `startDate` (that broke Edit vs Create).
   */
  const preserveWeeklyDaysFromPrefillRef = useRef(false);
  useLayoutEffect(() => {
    const iv = initialValues;
    const days = iv?.selectedDays;
    const ru = iv?.repeatUnit;
    if (ru === 'Week' && Array.isArray(days) && days.length > 1) {
      preserveWeeklyDaysFromPrefillRef.current = true;
    }
  }, [initialValues]);

  useEffect(() => {
    const id = setTimeout(() => {
      setDebouncedTrimmedScheduleName(String(scheduleName || '').trim());
    }, SCHEDULE_NAME_VALIDATE_DEBOUNCE_MS);
    return () => clearTimeout(id);
  }, [scheduleName]);

  const originalScheduleNameForValidation = useMemo(
    () => String(initialValues?.scheduleName || '').trim(),
    [initialValues?.scheduleName]
  );

  const {
    data: scheduleNameValidationData,
    isFetching: isScheduleNameValidating,
    isError: isScheduleNameValidationError,
    error: scheduleNameValidationError,
  } = useGenptScheduleValidateName(debouncedTrimmedScheduleName, {
    originalName: originalScheduleNameForValidation,
    enabled: !isReadOnly && !isDisabled,
  });

  const scheduleNameIsDuplicate = useMemo(() => {
    const baseline = originalScheduleNameForValidation.trim();
    const live = debouncedTrimmedScheduleName.trim();
    if (!live) return false;
    if (baseline !== '' && live === baseline) return false;
    const d = scheduleNameValidationData;
    return Boolean(
      d && d.success === true && d.available === false
    );
  }, [
    scheduleNameValidationData,
    originalScheduleNameForValidation,
    debouncedTrimmedScheduleName,
  ]);

  const scheduleNameFieldHelper = useMemo(() => {
    if (isReadOnly) return '';
    if (scheduleNameIsDuplicate) {
      return '';
    }
    const live = String(scheduleName || '').trim();
    if (
      isScheduleNameValidating &&
      live === debouncedTrimmedScheduleName &&
      debouncedTrimmedScheduleName
    ) {
      return 'Checking name…';
    }
    if (isScheduleNameValidationError && debouncedTrimmedScheduleName) {
      const msg =
        scheduleNameValidationError?.response?.data?.message ??
        scheduleNameValidationError?.message;
      return msg ? String(msg) : 'Could not verify name. You can still try to save.';
    }
    return '';
  }, [
    isReadOnly,
    scheduleNameIsDuplicate,
    scheduleName,
    isScheduleNameValidating,
    debouncedTrimmedScheduleName,
    isScheduleNameValidationError,
    scheduleNameValidationError,
  ]);

  /**
   * ROAR-4387: `useState` only seeds from `initialValues` on first mount. If the parent passes `{}` until
   * GET prefill lands, the schedule step stays on Week/MON/empty date — hydrate when real prefill appears.
   */
  useLayoutEffect(() => {
    const iv = initialValues || {};
    const propHasPrefill =
      String(iv.scheduleName || '').trim() !== '' ||
      (iv.startDate != null && String(iv.startDate).trim() !== '');
    const localLooksUnset =
      !String(scheduleName || '').trim() &&
      (!startDate || String(startDate).trim() === '');
    if (!propHasPrefill || !localLooksUnset) return;

    const sn = String(iv.scheduleName || '').trim();
    if (sn) setScheduleName(sn);

    setTimeZone(iv.timeZone || 'America/Los_Angeles');

    if (iv.startDate != null && iv.startDate !== '') setStartDate(iv.startDate);
    else if (!lockStartDate) setStartDate('');

    setStartHour(iv.startHour || '09');
    setStartMin(iv.startMin || '00');
    setStartAmPm(iv.startAmPm || 'AM');

    setRecurrencyType(iv.recurrencyType || 'custom');
    setRepeatValue(iv.repeatValue || '1');
    setRepeatUnit(iv.repeatUnit || 'Week');
    setWeeklyPattern(iv.weeklyPattern || 'specific');

    const d = iv.selectedDays;
    setSelectedDays(Array.isArray(d) && d.length > 0 ? [...d] : ['MON']);

    setMonthlyPattern(iv.monthlyPattern || 'specific_date');
    setMonthlySpecificDate(iv.monthlySpecificDate || '29th');
    setMonthlyRelativePosition(iv.monthlyRelativePosition || 'First');
    setMonthlyRelativeDay(iv.monthlyRelativeDay || 'Weekend');

    setEndsType(iv.endsType || 'never');
    setEndsOnDate(iv.endsOnDate || '2026-05-30');
    setEndsAfterCount(clampEndsAfterCountInitial(iv.endsAfterCount));

    setIsFreezingEnabled(iv.isFreezingEnabled || false);
    setFreezingPeriods(
      Array.isArray(iv.freezingPeriods) && iv.freezingPeriods.length > 0
        ? iv.freezingPeriods
        : [
            {
              id: 1,
              startDate: '',
              startTime: '09:00',
              startAmPm: 'AM',
              endDate: '',
              endTime: '05:00',
              endAmPm: 'PM',
            },
          ]
    );
  }, [initialValues, scheduleName, startDate, lockStartDate]);

  // Derive start-date details
  const { dayOfWeek, dayOfMonth, ordinal } = useMemo(() => {
    const d = new Date(`${startDate}T12:00:00`);
    if (isNaN(d.getTime())) return { dayOfWeek: 'Day', dayOfMonth: 1, ordinal: 'First' };
    const dow = d.toLocaleString('default', { weekday: 'long' });
    const dom = d.getDate();
    const ordinals = ['First', 'Second', 'Third', 'Fourth', 'Fifth'];
    const ordIdx = Math.ceil(dom / 7) - 1;
    return { dayOfWeek: dow, dayOfMonth: dom, ordinal: ordinals[ordIdx] || 'Last' };
  }, [startDate]);

  // When start date is set, align weekly "specific" days with that weekday (ROAR-4321: no sync while date empty)
  useEffect(() => {
    if (!startDate) return;
    if (repeatUnit !== 'Week' || weeklyPattern !== 'specific') return;
    if (preserveWeeklyDaysFromPrefillRef.current) return;
    const d = new Date(`${startDate}T12:00:00`);
    if (Number.isNaN(d.getTime())) return;
    const dow = d.toLocaleString('default', { weekday: 'long' });
    const shortDay = dow.substring(0, 3).toUpperCase();
    if (DAYS_OF_WEEK.includes(shortDay)) {
      setSelectedDays([shortDay]);
    }
  }, [startDate, repeatUnit, weeklyPattern]);

  /** ROAR-4321 issue 3: “Weekly on {day}” must not appear until start date exists; avoid invalid `matches` with no date. */
  useEffect(() => {
    if (!startDate && weeklyPattern === 'matches') {
      setWeeklyPattern('specific');
    }
  }, [startDate, weeklyPattern]);

  // ROAR-4320: one-off runs cannot use freezing periods
  useEffect(() => {
    if (recurrencyType === 'once') {
      setIsFreezingEnabled(false);
    }
  }, [recurrencyType]);

  // --- Repeat value handlers ---
  const handleRepeatValueChange = (val) => {
    if (val === '') { setRepeatValue(''); return; }
    const num = parseInt(val, 10);
    if (isNaN(num)) return;
    setRepeatValue(num.toString());
  };

  const handleRepeatValueBlur = () => {
    let num = parseInt(repeatValue, 10);
    const { min, max } = getRepeatLimits(repeatUnit);
    if (isNaN(num) || num < min) num = min;
    if (num > max) num = max;
    setRepeatValue(num.toString());
  };

  const handleRepeatUnitChange = (newUnit) => {
    setRepeatUnit(newUnit);
    let num = parseInt(repeatValue, 10);
    const { min, max } = getRepeatLimits(newUnit);
    if (!isNaN(num)) {
      if (num > max) setRepeatValue(max.toString());
      else if (num < min) setRepeatValue(min.toString());
    }
  };

  const handleEndsAfterCountChange = (e) => {
    const digits = String(e.target.value).replace(/\D/g, '').slice(0, 3);
    setEndsAfterCount(digits);
  };

  const handleEndsAfterCountBlur = () => {
    let n = parseInt(endsAfterCount, 10);
    if (Number.isNaN(n) || n < 1) n = 1;
    if (n > ENDS_AFTER_MAX_EXECUTIONS) n = ENDS_AFTER_MAX_EXECUTIONS;
    setEndsAfterCount(String(n));
  };

  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };

  // --- Edge case checks ---
  /** Wall clock is in `timeZone` (not browser local). Recomputes when TZ changes. */
  const isTimeInPast = useMemo(() => {
    if (!startDate || !timeZone) return false;
    let hr24 = parseInt(startHour, 10);
    const min = parseInt(startMin, 10) || 0;
    if (Number.isNaN(hr24)) hr24 = 0;
    if (startAmPm === 'PM' && hr24 !== 12) hr24 += 12;
    if (startAmPm === 'AM' && hr24 === 12) hr24 = 0;
    const wall = `${startDate} ${String(hr24).padStart(2, '0')}:${String(min).padStart(2, '0')}:00`;
    const selected = moment.tz(wall, 'YYYY-MM-DD HH:mm:ss', timeZone);
    if (!selected.isValid()) return false;
    const nowTz = moment.tz(timeZone);
    if (!selected.isSame(nowTz, 'day')) return false;
    return selected.isBefore(nowTz);
  }, [startDate, startHour, startMin, startAmPm, timeZone]);

  const isWeeklyMismatch =
    !!startDate &&
    recurrencyType === 'custom' &&
    repeatUnit === 'Week' &&
    weeklyPattern === 'specific' &&
    selectedDays.length > 0 &&
    !selectedDays.includes(dayOfWeek.substring(0, 3).toUpperCase());

  const isEndsBeforeStarts =
    recurrencyType === 'custom' &&
    endsType === 'on' &&
    endsOnDate &&
    startDate &&
    new Date(endsOnDate) < new Date(startDate);

  const isMonthlyDayWarning =
    recurrencyType === 'custom' &&
    repeatUnit === 'Month' &&
    monthlyPattern === 'specific_date' &&
    ['29th', '30th', '31st'].includes(monthlySpecificDate);

  // --- Summary computation ---
  const summary = useMemo(() => {
    if (!startDate || isEndsBeforeStarts)
      return { first: '-', last: '-', total: '-' };

    const freezeRanges =
      recurrencyType === 'custom' && isFreezingEnabled
        ? getFreezingWallMsRangesFromUi(freezingPeriods)
        : [];

    let hr24 = parseInt(startHour);
    if (startAmPm === 'PM' && hr24 !== 12) hr24 += 12;
    if (startAmPm === 'AM' && hr24 === 12) hr24 = 0;

    const baseDT = new Date(
      `${startDate}T${hr24.toString().padStart(2, '0')}:${startMin}:00`
    );
    if (isNaN(baseDT.getTime()))
      return { first: '-', last: '-', total: '-' };

    const interval = parseInt(repeatValue, 10) || 1;
    const isWeeklySpecificSequential =
      repeatUnit === 'Week' &&
      weeklyPattern === 'specific' &&
      selectedDays.length > 0 &&
      interval === 1;
    const minuteParsed = parseInt(startMin, 10) || 0;

    if (recurrencyType === 'once') {
      if (isTimeInPast) return { first: '-', last: '-', total: '-' };
      const when = formatDateTime(baseDT);
      return { first: when, last: when, total: '1' };
    }

    let firstExecutionDT = new Date(baseDT);

    if (repeatUnit === 'Month') {
      if (
        monthlyPattern === 'specific_date' ||
        monthlyPattern === 'matches_date'
      ) {
        const targetDay =
          monthlyPattern === 'specific_date'
            ? parseInt(monthlySpecificDate)
            : dayOfMonth;
        let current = new Date(baseDT);
        while (true) {
          const testDate = new Date(
            current.getFullYear(),
            current.getMonth(),
            targetDay,
            current.getHours(),
            current.getMinutes()
          );
          if (testDate.getMonth() === current.getMonth() && testDate >= baseDT) {
            firstExecutionDT = testDate;
            break;
          }
          current.setMonth(current.getMonth() + 1);
          current.setDate(1);
        }
      } else if (
        monthlyPattern === 'relative_day' ||
        monthlyPattern === 'matches_relative'
      ) {
        let current = new Date(baseDT);
        const targetPos =
          monthlyPattern === 'relative_day' ? monthlyRelativePosition : ordinal;
        const targetDayType =
          monthlyPattern === 'relative_day' ? monthlyRelativeDay : dayOfWeek;
        while (true) {
          const resolvedDate = resolveRelativeDay(
            current.getFullYear(),
            current.getMonth(),
            targetPos,
            targetDayType
          );
          if (resolvedDate) {
            resolvedDate.setHours(baseDT.getHours(), baseDT.getMinutes(), 0, 0);
            if (resolvedDate >= baseDT) {
              firstExecutionDT = resolvedDate;
              break;
            }
          }
          current.setMonth(current.getMonth() + 1);
          current.setDate(1);
        }
      }
    } else if (
      repeatUnit === 'Week' &&
      weeklyPattern === 'specific' &&
      selectedDays.length > 0
    ) {
      const selectedDayNums = selectedDays.map((d) => DAY_ABB_TO_JS_WEEKDAY[d]);
      let current = new Date(baseDT);
      for (let i = 0; i < 7; i++) {
        if (selectedDayNums.includes(current.getDay())) {
          firstExecutionDT = new Date(current);
          break;
        }
        current.setDate(current.getDate() + 1);
      }
    }

    const addInterval = (date) => {
      const next = new Date(date);
      if (repeatUnit === 'Month') {
        if (
          monthlyPattern === 'specific_date' ||
          monthlyPattern === 'matches_date'
        ) {
          const targetDay =
            monthlyPattern === 'specific_date'
              ? parseInt(monthlySpecificDate)
              : dayOfMonth;
          let attempts = 0;
          while (attempts < 12) {
            next.setMonth(next.getMonth() + interval);
            const testDate = new Date(
              next.getFullYear(),
              next.getMonth(),
              targetDay,
              next.getHours(),
              next.getMinutes()
            );
            if (testDate.getMonth() === next.getMonth()) return testDate;
            attempts++;
          }
        } else if (
          monthlyPattern === 'relative_day' ||
          monthlyPattern === 'matches_relative'
        ) {
          next.setMonth(next.getMonth() + interval);
          const targetPos =
            monthlyPattern === 'relative_day'
              ? monthlyRelativePosition
              : ordinal;
          const targetDayType =
            monthlyPattern === 'relative_day' ? monthlyRelativeDay : dayOfWeek;
          const resolved = resolveRelativeDay(
            next.getFullYear(),
            next.getMonth(),
            targetPos,
            targetDayType
          );
          if (resolved) {
            resolved.setHours(next.getHours(), next.getMinutes(), 0, 0);
            return resolved;
          }
        } else {
          next.setMonth(next.getMonth() + interval);
        }
      } else if (repeatUnit === 'Week') {
        next.setDate(next.getDate() + interval * 7);
      } else if (repeatUnit === 'Day') {
        next.setDate(next.getDate() + interval);
      } else if (repeatUnit === 'Year') {
        next.setFullYear(next.getFullYear() + interval);
      }
      return next;
    };

    const bumpSchedule = (fromDate) => {
      if (isWeeklySpecificSequential) {
        return nextWeeklySpecificSlotAfter(fromDate, selectedDays, hr24, minuteParsed);
      }
      return addInterval(fromDate);
    };

    /** ROAR-4327: Day/Year used baseDT as first slot, so a start time earlier “today” still counted as a run inside “Ends on” even though it already passed. Advance to the next occurrence ≥ now (same as real scheduler behavior), then week/month edge cases stay correct too. */
    /** ROAR-4367 / ROAR-4368: Weekly “specific days” + every 1 week must hop to the next selected weekday (not +7 days), or the first run jumps past “Ends on” (zero executions) / later same-week runs are skipped (total stuck at 1). */
    const now = new Date();
    if (isWeeklySpecificSequential && firstExecutionDT < now) {
      const bumped = nextWeeklySpecificSlotOnOrAfter(now, selectedDays, hr24, minuteParsed);
      if (bumped) firstExecutionDT = bumped;
    }
    let advanceGuard = 0;
    while (!isWeeklySpecificSequential && firstExecutionDT < now && advanceGuard < 10000) {
      const nextFe = addInterval(new Date(firstExecutionDT));
      if (nextFe.getTime() <= firstExecutionDT.getTime()) {
        break;
      }
      firstExecutionDT = nextFe;
      advanceGuard += 1;
    }
    if (firstExecutionDT < now) {
      return { first: '-', last: '-', total: '0', zeroReason: 'pattern' };
    }

    const first = formatDateTime(firstExecutionDT);

    if (endsType === 'on' && endsOnDate) {
      const endLimit = new Date(`${endsOnDate}T23:59:59`);
      if (firstExecutionDT > endLimit) {
        return { first: '-', last: '-', total: '0', zeroReason: 'pattern' };
      }
    }

    const dates = [new Date(firstExecutionDT)];
    let current = new Date(firstExecutionDT);

    if (endsType === 'after') {
      const totalCount = parseInt(endsAfterCount) || 1;
      if (freezeRanges.length > 0) {
        const nominalDates = [new Date(firstExecutionDT)];
        let curN = new Date(firstExecutionDT);
        for (let i = 1; i < totalCount; i++) {
          const nxt = bumpSchedule(curN);
          if (!nxt || nxt.getTime() <= curN.getTime()) break;
          curN = nxt;
          nominalDates.push(curN);
        }
        const effectiveDates = nominalDates.map(
          (d) => new Date(resolveMsThroughFreezingRanges(d.getTime(), freezeRanges))
        );
        const totalStr = effectiveDates.length.toString();
        const zeroReason =
          totalStr === '0' && nominalDates.length > 0
            ? 'freeze'
            : totalStr === '0'
              ? 'pattern'
              : undefined;
        return {
          first: formatDateTime(effectiveDates[0]),
          last:
            effectiveDates.length > 1
              ? formatDateTime(effectiveDates[effectiveDates.length - 1])
              : '-',
          total: totalStr,
          ...(zeroReason ? { zeroReason } : {}),
        };
      }
      for (let i = 1; i < totalCount; i++) {
        const nxt = bumpSchedule(current);
        if (!nxt || nxt.getTime() <= current.getTime()) break;
        current = nxt;
        dates.push(current);
      }
      return {
        first,
        last: dates.length > 1 ? formatDateTime(dates[dates.length - 1]) : '-',
        total: dates.length.toString(),
      };
    }

    if (endsType === 'on') {
      const endLimit = new Date(`${endsOnDate}T23:59:59`);
      if (freezeRanges.length > 0) {
        let nominalInWindow = 1;
        let cN = new Date(firstExecutionDT);
        let sN = 0;
        while (cN < endLimit && sN < 1000) {
          const nxtN = bumpSchedule(cN);
          if (!nxtN || nxtN.getTime() <= cN.getTime()) break;
          cN = nxtN;
          if (cN <= endLimit) nominalInWindow += 1;
          sN += 1;
        }
        const effective = [];
        let curE = new Date(firstExecutionDT);
        let sanityCheck = 0;
        while (sanityCheck < 1000) {
          sanityCheck += 1;
          if (curE > endLimit) break;
          const resolvedMs = resolveMsThroughFreezingRanges(
            curE.getTime(),
            freezeRanges
          );
          if (resolvedMs <= endLimit.getTime()) {
            effective.push(new Date(resolvedMs));
          }
          const nxt = bumpSchedule(curE);
          if (!nxt || nxt.getTime() <= curE.getTime()) break;
          curE = nxt;
        }
        const totalStr = effective.length.toString();
        const zeroReason =
          totalStr === '0' && nominalInWindow > 0
            ? 'freeze'
            : totalStr === '0'
              ? 'pattern'
              : undefined;
        return {
          first: effective[0] ? formatDateTime(effective[0]) : '-',
          last:
            effective.length > 1
              ? formatDateTime(effective[effective.length - 1])
              : '-',
          total: totalStr,
          ...(zeroReason ? { zeroReason } : {}),
        };
      }
      let sanityCheck = 0;
      while (current < endLimit && sanityCheck < 1000) {
        const nxt = bumpSchedule(current);
        if (!nxt || nxt.getTime() <= current.getTime()) break;
        current = nxt;
        if (current <= endLimit) dates.push(current);
        sanityCheck++;
      }
      return {
        first,
        last: dates.length > 1 ? formatDateTime(dates[dates.length - 1]) : '-',
        total: dates.length.toString(),
      };
    }

    return { first, last: '-', total: 'Ongoing' };
  }, [
    startDate, startHour, startMin, startAmPm, recurrencyType,
    repeatValue, repeatUnit, weeklyPattern, selectedDays,
    monthlyPattern, monthlySpecificDate, monthlyRelativePosition,
    monthlyRelativeDay, endsType, endsOnDate, endsAfterCount,
    dayOfMonth, dayOfWeek, ordinal, isEndsBeforeStarts, isTimeInPast,
    isFreezingEnabled, freezingPeriods,
  ]);

  const hasZeroExecutions =
    recurrencyType === 'custom' && summary.total === '0';
  /** ROAR-4325: end on / end after caps repetitions so only the first run occurs */
  const showSingleExecutionEndWarning =
    recurrencyType === 'custom' &&
    endsType !== 'never' &&
    Boolean(startDate) &&
    !isEndsBeforeStarts &&
    !hasZeroExecutions &&
    summary.total === '1';
  const isTimeError = isTimeInPast && recurrencyType === 'once';
  const scheduleNameCheckBlocking =
    !isReadOnly &&
    !!debouncedTrimmedScheduleName &&
    (isScheduleNameValidating || scheduleNameIsDuplicate);

  const [scheduleTimeConflict, setScheduleTimeConflict] = useState(false);
  const [scheduleTimeConflictLoading, setScheduleTimeConflictLoading] = useState(false);

  useEffect(() => {
    if (isReadOnly || isDisabled) {
      setScheduleTimeConflict(false);
      setScheduleTimeConflictLoading(false);
      return undefined;
    }
    const aid =
      assetId != null && String(assetId).trim() !== '' ? String(assetId).trim() : null;
    if (!aid || !startDate) {
      setScheduleTimeConflict(false);
      setScheduleTimeConflictLoading(false);
      return undefined;
    }

    const ac = new AbortController();
    setScheduleTimeConflictLoading(true);
    const t = setTimeout(async () => {
      const draftKey = buildGenptScheduleTimeConflictKey(
        {
          timeZone,
          startHour,
          startMin,
          startAmPm,
          startDate,
          recurrencyType,
          repeatValue,
          repeatUnit,
          weeklyPattern,
          selectedDays,
          monthlyPattern,
          monthlySpecificDate,
          monthlyRelativePosition,
          monthlyRelativeDay,
        },
        startDate
      );
      if (!draftKey) {
        if (!ac.signal.aborted) {
          setScheduleTimeConflict(false);
          setScheduleTimeConflictLoading(false);
        }
        return;
      }
      try {
        const conflict = await hasGenptScheduleSlotConflict({
          assetId: aid,
          scheduleUi: {
            timeZone,
            startHour,
            startMin,
            startAmPm,
            startDate,
            recurrencyType,
            repeatValue,
            repeatUnit,
            weeklyPattern,
            selectedDays,
            monthlyPattern,
            monthlySpecificDate,
            monthlyRelativePosition,
            monthlyRelativeDay,
          },
          excludeScheduleId,
          signal: ac.signal,
        });
        if (ac.signal.aborted) return;
        setScheduleTimeConflict(Boolean(conflict));
      } catch {
        if (!ac.signal.aborted) {
          setScheduleTimeConflict(false);
        }
      } finally {
        if (!ac.signal.aborted) {
          setScheduleTimeConflictLoading(false);
        }
      }
    }, SCHEDULE_TIME_CONFLICT_DEBOUNCE_MS);

    return () => {
      ac.abort();
      clearTimeout(t);
    };
  }, [
    assetId,
    excludeScheduleId,
    isDisabled,
    isReadOnly,
    monthlyPattern,
    monthlyRelativeDay,
    monthlyRelativePosition,
    monthlySpecificDate,
    recurrencyType,
    repeatUnit,
    repeatValue,
    startAmPm,
    startDate,
    startHour,
    startMin,
    timeZone,
    weeklyPattern,
    selectedDays,
  ]);

  const scheduleTimeConflictBlocking =
    !isReadOnly && (scheduleTimeConflictLoading || scheduleTimeConflict);

  const hasFreezingPeriodOverlap = useMemo(() => {
    if (!isFreezingEnabled || recurrencyType === 'once') return false;
    return freezingPeriodsOverlapFromUi(freezingPeriods);
  }, [isFreezingEnabled, recurrencyType, freezingPeriods]);

  const disableSubmit =
    isEndsBeforeStarts ||
    hasZeroExecutions ||
    isTimeError ||
    !scheduleName.trim() ||
    !startDate ||
    scheduleNameCheckBlocking ||
    scheduleTimeConflictBlocking ||
    hasFreezingPeriodOverlap;

  // --- Freezing period handlers ---
  const handleAddFreezingPeriod = () => {
    if (freezingPeriods.length >= 3) return;
    const lastPeriod = freezingPeriods[freezingPeriods.length - 1];
    let newStartDate = '';
    if (lastPeriod?.endDate) {
      const nextDay = new Date(lastPeriod.endDate);
      nextDay.setDate(nextDay.getDate() + 1);
      newStartDate = nextDay.toISOString().split('T')[0];
    }
    setFreezingPeriods([
      ...freezingPeriods,
      {
        id: Date.now(),
        startDate: newStartDate,
        startTime: '09:00',
        startAmPm: 'AM',
        endDate: '',
        endTime: '05:00',
        endAmPm: 'PM',
      },
    ]);
  };

  const updateFreezingPeriod = (index, field, value) => {
    const updated = [...freezingPeriods];
    updated[index] = { ...updated[index], [field]: value };

    if (field === 'startDate' && value) {
      const start = new Date(value);
      const maxEnd = new Date(start);
      maxEnd.setDate(maxEnd.getDate() + 30);
      const currentEnd = new Date(updated[index].endDate);
      if (!updated[index].endDate || currentEnd > maxEnd || currentEnd < start) {
        updated[index].endDate = maxEnd.toISOString().split('T')[0];
      }
    }

    setFreezingPeriods(updated);
  };

  const removeFreezingPeriod = (indexToRemove) => {
    setFreezingPeriods(freezingPeriods.filter((_, i) => i !== indexToRemove));
  };

  const getMaxEndDate = (startDateStr) => {
    if (!startDateStr) return undefined;
    const d = new Date(startDateStr);
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  };

  const getMinStartDate = (index) => {
    if (index === 0) return undefined;
    const prevEndDate = freezingPeriods[index - 1].endDate;
    if (!prevEndDate) return undefined;
    const minD = new Date(prevEndDate);
    minD.setDate(minD.getDate() + 1);
    return minD.toISOString().split('T')[0];
  };

  // --- Submit handler ---
  const handleSubmit = () => {
    if (!isReadOnly && scheduleTimeConflictBlocking) return;
    if (isFreezingEnabled && recurrencyType !== 'once' && freezingPeriodsOverlapFromUi(freezingPeriods)) {
      return;
    }
    const freezingAllowed = recurrencyType !== 'once' && isFreezingEnabled;
    onSubmit({
      scheduleName,
      timeZone,
      startDate,
      startHour,
      startMin,
      startAmPm,
      recurrencyType,
      repeatValue,
      repeatUnit,
      weeklyPattern,
      selectedDays,
      monthlyPattern,
      monthlySpecificDate,
      monthlyRelativePosition,
      monthlyRelativeDay,
      endsType,
      endsOnDate,
      endsAfterCount,
      isFreezingEnabled: freezingAllowed,
      freezingPeriods: freezingAllowed ? freezingPeriods : [],
      summary,
    });
  };

  const allDisabled = isReadOnly || isDisabled;
  const startDateLocked = lockStartDate && !isReadOnly;
  const startDatePickerMin = useMemo(() => {
    if (startDateLocked && startDate) {
      const m = moment(startDate, 'YYYY-MM-DD', true);
      if (m.isValid()) return m.startOf('day');
    }
    return moment().startOf('day');
  }, [startDateLocked, startDate]);

  // --- Render helpers ---

  const renderTimeInputs = (hour, setHour, min, setMin, ampm, setAmPm) => {
    /** Externally-created schedules (e.g. CICD pipelines hitting the API directly) can persist a
     *  start minute that isn't one of the 15-minute slots the UI offers — e.g. `04:05:36` arrives
     *  as `startMin = '05'`. MUI Select renders empty when its `value` doesn't match any MenuItem,
     *  which is what the user is reporting. Surface the off-grid value as a one-off option so the
     *  field renders the actual saved minute and round-trips without silently shifting time on
     *  re-save. Freezing-period inputs already pre-snap via `normalizeTimeSelectParts`, so this
     *  branch is inert for them. */
    const minStr = String(min ?? '');
    const isOffGridMin =
      /^[0-5]\d$/.test(minStr) && !MINUTE_OPTIONS.includes(minStr);
    const minuteOptions = isOffGridMin
      ? [...MINUTE_OPTIONS, minStr].sort()
      : MINUTE_OPTIONS;

    /** Same defense for HR — `parseTestStartTimeParts` already coerces to 1–12, but if an
     *  upstream prefill ever feeds `00` / `13+` directly, the dropdown would render blank too. */
    const hourStr = String(hour ?? '');
    const isOffGridHour =
      /^\d{1,2}$/.test(hourStr) && !HOUR_OPTIONS.includes(hourStr.padStart(2, '0'));
    const hourOptions = isOffGridHour
      ? [...HOUR_OPTIONS, hourStr.padStart(2, '0')].sort()
      : HOUR_OPTIONS;

    return (
    <div className="flex flex-wrap items-start gap-3 sm:gap-4">
      <div className="min-w-0">
        <label className="block text-sm font-medium text-gray-700 mb-1">HR</label>
        <MuiTextField
          select
          value={hour}
          onChange={(e) => setHour(e.target.value)}
          margin="none"
          fullWidth={false}
          className="!w-[4.25rem] !m-0"
          size="small"
          disabled={allDisabled}
          SelectProps={SELECT_MENU_SCROLL}
        >
          {hourOptions.map((h) => (
            <MenuItem key={h} value={h}>{h}</MenuItem>
          ))}
        </MuiTextField>
      </div>
      <div className="min-w-0">
        <label className="block text-sm font-medium text-gray-700 mb-1">MIN</label>
        <MuiTextField
          select
          value={min}
          onChange={(e) => setMin(e.target.value)}
          margin="none"
          fullWidth={false}
          className="!w-[4.25rem] !m-0"
          size="small"
          disabled={allDisabled}
          SelectProps={SELECT_MENU_SCROLL}
        >
          {minuteOptions.map((m) => (
            <MenuItem key={m} value={m}>{m}</MenuItem>
          ))}
        </MuiTextField>
      </div>
      <div className="min-w-0">
        <label className="block text-sm font-medium text-gray-700 mb-1">AM/PM</label>
        <div className="flex h-10 flex-shrink-0 overflow-hidden rounded-md border border-gray-300">
          <button
            type="button"
            disabled={allDisabled}
            className={`min-w-[44px] px-2.5 text-sm font-semibold transition-colors ${
              ampm === 'AM'
                ? 'bg-primary-50 text-primary-500'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setAmPm('AM')}
          >
            AM
          </button>
          <button
            type="button"
            disabled={allDisabled}
            className={`min-w-[44px] px-2.5 text-sm font-semibold border-l border-gray-300 transition-colors ${
              ampm === 'PM'
                ? 'bg-primary-50 text-primary-500'
                : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
            onClick={() => setAmPm('PM')}
          >
            PM
          </button>
        </div>
      </div>
    </div>
    );
  };

  const renderWeeklyOptions = () => (
    <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Weekly Pattern
        </label>
        <MuiTextField
          select
          value={weeklyPattern}
          onChange={(e) => setWeeklyPattern(e.target.value)}
          margin="none"
          fullWidth={false}
          className="!m-0 !max-w-[min(100%,20rem)]"
          size="small"
          disabled={allDisabled}
          SelectProps={SELECT_MENU_SCROLL}
        >
          {startDate ? (
            <MenuItem value="matches">Weekly on {dayOfWeek}</MenuItem>
          ) : null}
          <MenuItem value="specific">
            On specific days of the week
          </MenuItem>
        </MuiTextField>
      </div>
      {weeklyPattern === 'specific' && (
        <div className="flex flex-wrap gap-3 mt-3">
          {DAYS_OF_WEEK.map((day) => (
            <button
              key={day}
              type="button"
              disabled={allDisabled}
              onClick={() => toggleDay(day)}
              className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                selectedDays.includes(day)
                  ? 'bg-primary-50 border-primary-400 text-primary-600 font-semibold'
                  : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
              }`}
            >
              {day}
            </button>
          ))}
        </div>
      )}

      {!isReadOnly && isWeeklyMismatch && (
        <div className="mt-3 flex items-start space-x-2 text-xs text-gray-700 bg-[#FFF3E0] p-2.5 rounded">
          <WarningAmberIcon
            sx={{ fontSize: 16, color: '#EAB308', flexShrink: 0, marginTop: '2px' }}
          />
          <p>
            <strong>Warning:</strong> Your start date is a{' '}
            <strong>{dayOfWeek}</strong>, but it is not selected above. The
            first run will automatically defer to the next selected day.
          </p>
        </div>
      )}
    </div>
  );

  const renderMonthlyOptions = () => (
    <div className="mt-4 space-y-4 border-t border-gray-200 pt-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1.5">
          Monthly Pattern
        </label>
        <MuiTextField
          select
          value={monthlyPattern}
          onChange={(e) => setMonthlyPattern(e.target.value)}
          margin="none"
          fullWidth={false}
          className="!m-0 !max-w-[min(100%,22rem)]"
          size="small"
          disabled={allDisabled}
          SelectProps={SELECT_MENU_SCROLL}
        >
          <MenuItem value="matches_date">
            Monthly on day {dayOfMonth}
          </MenuItem>
          <MenuItem value="matches_relative">
            Monthly on {ordinal} {dayOfWeek}
          </MenuItem>
          <MenuItem value="relative_day">
            On relative time of the month
          </MenuItem>
          <MenuItem value="specific_date">
            On a specific calendar date
          </MenuItem>
        </MuiTextField>
      </div>

      {monthlyPattern === 'specific_date' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">
            Select Date
          </label>
          <MuiTextField
            select
            value={monthlySpecificDate}
            onChange={(e) => setMonthlySpecificDate(e.target.value)}
            margin="none"
            fullWidth={false}
            className="!w-[5.5rem] !m-0"
            size="small"
            disabled={allDisabled}
            SelectProps={SELECT_MENU_SCROLL}
          >
            {MONTHLY_DATE_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </MuiTextField>
        </div>
      )}

      {monthlyPattern === 'relative_day' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Select</label>
            <MuiTextField
              select
              value={monthlyRelativePosition}
              onChange={(e) => setMonthlyRelativePosition(e.target.value)}
              margin="none"
              fullWidth
              className="!m-0"
              size="small"
              disabled={allDisabled}
              SelectProps={SELECT_MENU_SCROLL}
            >
              {['First', 'Last'].map((pos) => (
                <MenuItem key={pos} value={pos}>{pos}</MenuItem>
              ))}
            </MuiTextField>
          </div>
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Day type</label>
            <MuiTextField
              select
              value={monthlyRelativeDay}
              onChange={(e) => setMonthlyRelativeDay(e.target.value)}
              margin="none"
              fullWidth
              className="!m-0"
              size="small"
              disabled={allDisabled}
              SelectProps={SELECT_MENU_SCROLL}
            >
              {['Day', 'Weekday', 'Weekend'].map((d) => (
                <MenuItem key={d} value={d}>{d}</MenuItem>
              ))}
            </MuiTextField>
          </div>
        </div>
      )}

      {!isReadOnly && isMonthlyDayWarning && (
        <div className="mt-3 flex items-start space-x-2 text-xs text-gray-700 bg-[#FFF3E0] p-2.5 rounded">
          <WarningAmberIcon
            sx={{ fontSize: 16, color: '#EAB308', flexShrink: 0, marginTop: '2px' }}
          />
          <p>
            <strong>Warning:</strong> Months that don&apos;t include the selected
            date (like February) will be skipped. To run the test on the last day
            of every month, choose the{' '}
            <strong>Monthly → Relative day → Last day</strong> option.
          </p>
        </div>
      )}
    </div>
  );

  return (
    <div className={allDisabled ? 'opacity-75 pointer-events-none' : ''}>
      <div className="space-y-5">
        {/* Basic Info — top-align so Schedule Name + Time Zone stay fixed when helper / validate text appears (ROAR-4360) */}
        <div className="grid grid-cols-1 md:grid-cols-2 md:items-start gap-x-6 gap-y-4">
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Schedule Name <span className="text-red-500">*</span>
            </label>
            <MuiTextField
              value={scheduleName}
              onChange={(e) => setScheduleName(e.target.value)}
              placeholder="Name your schedule"
              margin="none"
              fullWidth
              className="!m-0"
              size="small"
              disabled={allDisabled}
              error={!isReadOnly && scheduleNameIsDuplicate}
              helperText={scheduleNameFieldHelper || undefined}
              FormHelperTextProps={{
                className: '!mx-0 !w-full !max-w-none',
              }}
            />
          </div>
          <div className="min-w-0">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Time Zone
            </label>
            <MuiTextField
              select
              value={timeZone}
              onChange={(e) => setTimeZone(e.target.value)}
              margin="none"
              fullWidth={false}
              className="!m-0 w-full max-w-[260px]"
              size="small"
              disabled={allDisabled}
              SelectProps={SELECT_MENU_SCROLL}
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <MenuItem key={tz.value} value={tz.value}>
                  {tz.label}
                </MenuItem>
              ))}
            </MuiTextField>
          </div>
          {!isReadOnly && scheduleNameIsDuplicate && (
            <div className="min-w-0 md:col-span-2 flex w-full max-w-full items-center gap-2 rounded border border-[#F65C5C]/30 bg-[#F65C5C]/10 p-2.5 text-xs text-red-800">
              <ScheduleSectionErrorIcon />
              <p className="m-0 min-w-0 flex-1 max-w-full whitespace-normal break-words leading-snug">
                <span className="font-semibold text-gray-900">Error:</span>{' '}
                A schedule with this name already exists. Choose a different name.
              </p>
            </div>
          )}
        </div>

        {/* Execution Schedule */}
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4">
          <div className="text-base font-semibold text-gray-800 mb-3">
            Execution Schedule
          </div>

          <div className="flex flex-col gap-4 mb-4">
            {/* Start Date */}
            <div className="w-full md:max-w-[48%]">
              <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5 gap-1">
                Start Date
                <Info
                  tooltip={
                    startDateLocked
                      ? 'The schedule start date cannot be changed when editing an existing schedule. Other execution settings can still be updated.'
                      : 'This date determines when the scheduled test starts. For Run only once, the test runs on this date. For recurring schedules, future runs follow the selected pattern.'
                  }
                />
              </label>
              <MuiDatePicker
                label=""
                value={startDate ? moment(startDate, 'YYYY-MM-DD') : null}
                minDate={startDatePickerMin}
                disabled={allDisabled || startDateLocked}
                inputFormat="MM/DD/YYYY"
                onChange={(date) => {
                  if (startDateLocked) return;
                  if (date && date.isValid()) {
                    setStartDate(date.format('YYYY-MM-DD'));
                  } else {
                    setStartDate('');
                  }
                }}
                textFieldProps={{
                  size: 'small',
                  margin: 'none',
                  className: '!m-0',
                  placeholder: 'Select start date',
                }}
              />
            </div>

            {/* Start Time */}
            <div className="w-full">
              <div className="w-full md:max-w-[48%]">
                <label className="flex items-center text-sm font-medium text-gray-700 mb-1.5 gap-1">
                  Test Start Time
                  <Info tooltip="This time determines when the scheduled test starts. Time slots are available in 15-minute intervals." />
                </label>
                {renderTimeInputs(
                  startHour, setStartHour,
                  startMin, setStartMin,
                  startAmPm, setStartAmPm
                )}
              </div>

              {!isReadOnly && isTimeInPast && recurrencyType === 'once' && (
                <div className="mt-3 flex w-full items-center gap-2 text-xs text-red-800 bg-[#F65C5C]/10 p-2.5 rounded border border-[#F65C5C]/30">
                  <ScheduleSectionErrorIcon />
                  <p className="m-0 min-w-0 flex-1 leading-snug">
                    <span className="font-semibold text-gray-900">Error:</span>{' '}
                    The selected time has already passed. Please choose a future time
                    to schedule the test.
                  </p>
                </div>
              )}
              {/* ROAR-4314: past time today + recurring — warning if a future slot exists, error only when none */}
              {!isReadOnly && isTimeInPast && recurrencyType === 'custom' && !hasZeroExecutions && (
                <div className="mt-3 flex w-full items-start gap-2 rounded border border-[#FFE0B2]/70 bg-[#FFF3E0] p-2.5 text-xs text-gray-700">
                  <WarningAmberIcon
                    className="flex-shrink-0"
                    sx={{ fontSize: 18, color: '#EAB308', marginTop: '1px' }}
                  />
                  <p className="m-0 min-w-0 flex-1 leading-snug">
                    <strong>Warning:</strong> This start time has already passed today. Future runs
                    are still possible; the first execution uses the next valid slot (see schedule
                    summary).
                  </p>
                </div>
              )}
              {!isReadOnly && isTimeInPast && recurrencyType === 'custom' && hasZeroExecutions && (
                <div className="mt-3 flex w-full items-start gap-2 text-xs text-red-800 bg-[#F65C5C]/10 p-2.5 rounded border border-[#F65C5C]/30">
                  <ScheduleSectionErrorIcon className="mt-0.5" />
                  <p className="m-0 min-w-0 flex-1 leading-snug">
                    <span className="font-semibold text-gray-900">Error:</span>{' '}
                    This time has already passed today and there is no valid future occurrence with
                    the current pattern and end settings. Adjust the start time, recurrence, or end
                    rules.
                  </p>
                </div>
              )}
              {!isReadOnly && assetId && scheduleTimeConflictLoading && (
                <div className="mt-3 flex w-full items-center gap-2 text-xs text-gray-600 bg-gray-50 p-2.5 rounded border border-gray-200">
                  <span className="font-medium">Checking for schedule time conflicts…</span>
                </div>
              )}
              {!isReadOnly && assetId && !scheduleTimeConflictLoading && scheduleTimeConflict && (
                <div className="mt-3 flex w-full items-center gap-2 rounded border border-[#F65C5C]/30 bg-[#F65C5C]/10 p-2.5 text-xs text-red-800">
                  <ScheduleSectionErrorIcon />
                  <p className="m-0 min-w-0 flex-1 leading-snug">
                    <span className="font-semibold text-gray-900">Error:</span> This asset is already
                    scheduled for a test at the selected date and time. Please choose a different slot.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Recurrency */}
          <div className="mb-2">
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2.5 gap-1">
              Recurrency
              <Info tooltip="This defines how often the test runs. Choose to run once or set a recurring schedule (daily, weekly, or monthly). Runs that fall outside valid dates will be skipped." />
            </label>
            <div className="flex flex-wrap gap-3 mb-4">
              <button
                type="button"
                disabled={allDisabled}
                onClick={() => setRecurrencyType('custom')}
                className={`px-4 py-2 rounded-md border text-sm font-semibold transition-colors ${
                  recurrencyType === 'custom'
                    ? 'border-primary-200 bg-primary-50 text-primary-600'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Custom Schedule
              </button>
              <button
                type="button"
                disabled={allDisabled}
                onClick={() => setRecurrencyType('once')}
                className={`px-4 py-2 rounded-md border text-sm font-semibold transition-colors ${
                  recurrencyType === 'once'
                    ? 'border-primary-200 bg-primary-50 text-primary-600'
                    : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                }`}
              >
                Run Only Once
              </button>
            </div>

            {recurrencyType === 'custom' && (
              <div className="rounded-lg border border-gray-200 bg-white p-3 mb-4">
                <div className="flex flex-wrap items-end gap-3">
                  <span className="text-sm font-medium text-gray-700 pb-2">Repeat Every</span>
                  <input
                    type="number"
                    value={repeatValue}
                    onChange={(e) => handleRepeatValueChange(e.target.value)}
                    onBlur={handleRepeatValueBlur}
                    disabled={allDisabled}
                    className="w-14 border border-gray-300 rounded-md py-1.5 px-2 text-sm font-medium disabled:opacity-50"
                    min={getRepeatLimits(repeatUnit).min}
                    max={getRepeatLimits(repeatUnit).max}
                  />
                  <MuiTextField
                    select
                    value={repeatUnit}
                    onChange={(e) => handleRepeatUnitChange(e.target.value)}
                    margin="none"
                    fullWidth={false}
                    className="!w-[5.75rem] !m-0"
                    size="small"
                    disabled={allDisabled}
                    SelectProps={SELECT_MENU_SCROLL}
                  >
                    {REPEAT_UNIT_OPTIONS.map((u) => (
                      <MenuItem key={u} value={u}>{u}</MenuItem>
                    ))}
                  </MuiTextField>
                </div>

                {repeatUnit === 'Week' && renderWeeklyOptions()}
                {repeatUnit === 'Month' && renderMonthlyOptions()}
              </div>
            )}

            {/* Ends configuration */}
            {recurrencyType === 'custom' && (
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2.5 gap-1">
                  Ends
                  <Info tooltip="Determines when the recurring schedule should stop executing." />
                </label>
                <div className="flex flex-wrap gap-3 mb-3">
                  {['never', 'on', 'after'].map((type) => (
                    <button
                      key={type}
                      type="button"
                      disabled={allDisabled}
                      onClick={() => setEndsType(type)}
                      className={`px-4 py-2 rounded-md border text-sm font-semibold transition-colors capitalize ${
                        endsType === type
                          ? 'border-primary-300 bg-primary-50 text-primary-600'
                          : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {type}
                    </button>
                  ))}
                </div>

                {endsType === 'on' && (
                  <div>
                    <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-md border border-gray-200 w-fit max-w-full">
                      <span className="text-sm text-gray-600">End on</span>
                      <MuiDatePicker
                        label=""
                        value={
                          endsOnDate
                            ? moment(endsOnDate, 'YYYY-MM-DD')
                            : null
                        }
                        minDate={moment(startDate, 'YYYY-MM-DD')}
                        disabled={allDisabled}
                        inputFormat="MM/DD/YYYY"
                        onChange={(date) => {
                          if (date && date.isValid()) {
                            setEndsOnDate(date.format('YYYY-MM-DD'));
                          }
                        }}
                        textFieldProps={{
                          size: 'small',
                          margin: 'none',
                          className: '!m-0 !w-[11rem]',
                          error: !isReadOnly && isEndsBeforeStarts,
                        }}
                      />
                    </div>

                    {!isReadOnly && isEndsBeforeStarts && (
                      <div className="mt-3 flex items-start gap-2 text-xs text-red-800 bg-[#F65C5C]/10 p-2.5 rounded border border-[#F65C5C]/30">
                        <ScheduleSectionErrorIcon className="mt-0.5" />
                        <p className="m-0 min-w-0 flex-1 leading-snug">
                          <span className="font-semibold text-gray-900">Error:</span>{' '}
                          The end date cannot be earlier than the start date.
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {endsType === 'after' && (
                  <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-md border border-gray-200 w-fit max-w-full">
                    <span className="text-sm font-medium text-gray-700">End after</span>
                    <input
                      type="text"
                      inputMode="numeric"
                      autoComplete="off"
                      maxLength={3}
                      value={endsAfterCount}
                      onChange={handleEndsAfterCountChange}
                      onBlur={handleEndsAfterCountBlur}
                      disabled={allDisabled}
                      className="w-16 border border-gray-300 rounded-md py-1.5 px-2 text-sm disabled:opacity-50"
                      aria-label="End after number of executions"
                    />
                    <span className="text-sm text-gray-600">executions</span>
                  </div>
                )}

                {!isReadOnly &&
                  hasZeroExecutions &&
                  !isEndsBeforeStarts &&
                  summary.zeroReason !== 'freeze' && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-red-800 bg-[#F65C5C]/10 p-2.5 rounded border border-[#F65C5C]/30">
                    <ScheduleSectionErrorIcon className="mt-0.5" />
                    <p className="m-0 min-w-0 flex-1 leading-snug">
                      <span className="font-semibold text-gray-900">Error:</span>{' '}
                      This recurring pattern exceeds your &apos;Ends On&apos; date before a single
                      execution can run. Please extend the end date or decrease the repetition
                      interval.
                    </p>
                  </div>
                )}

                {!isReadOnly && showSingleExecutionEndWarning && (
                  <div className="mt-3 flex items-start gap-2 text-xs text-gray-700 bg-[#FFF3E0] p-2.5 rounded border border-[#FFE0B2]/70">
                    <WarningAmberIcon
                      sx={{ fontSize: 18, color: '#EAB308', flexShrink: 0, marginTop: '2px' }}
                    />
                    <p className="m-0 leading-relaxed">{SINGLE_EXECUTION_END_WARNING}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Freezing Period — plain section (no panel); Schedule Summary below keeps grey card */}
        <div className="mt-5">
          <div className="mb-2">
            <div className="flex items-start gap-2">
              <AcUnitIcon
                sx={{ fontSize: 20 }}
                className={`shrink-0 mt-0.5 ${
                  recurrencyType === 'once' ? 'text-gray-400' : 'text-primary-500'
                }`}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2 text-sm font-semibold">
                  <span
                    className={
                      recurrencyType === 'once' ? 'text-gray-500' : 'text-gray-800'
                    }
                  >
                    Freezing Period
                  </span>
                  <Info tooltip="Set blackout windows when tests should not run (e.g., holidays, maintenance). Define up to 3 periods, 30 days each." />
                  {recurrencyType === 'once' && (
                    <span
                      className="inline-flex shrink-0 items-center border px-2.5 py-0.5 text-xs font-semibold rounded-[2px]"
                      style={{
                        borderColor: '#FEA42B',
                        backgroundColor: '#fff3e0',
                        color: '#FEA42B',
                      }}
                    >
                      {"Disabled for 'Run Only Once' Schedules"}
                    </span>
                  )}
                </div>
                <p
                  className={`text-sm mt-1.5 leading-relaxed ${
                    recurrencyType === 'once' ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  Set blackout windows when tests should not run (e.g., holidays, maintenance). Define
                  up to 3 periods, 30 days each.
                </p>
              </div>
            </div>
          </div>

          <FormControlLabel
            control={
              <Checkbox
                checked={recurrencyType === 'once' ? false : isFreezingEnabled}
                onChange={(e) => setIsFreezingEnabled(e.target.checked)}
                disabled={allDisabled || recurrencyType === 'once'}
                sx={{ '&.Mui-checked': { color: '#228EBC' } }}
              />
            }
            label={
              <span
                className={`text-sm font-medium ${
                  recurrencyType === 'once' ? 'text-gray-400' : 'text-gray-800'
                }`}
              >
                Enable freezing periods
              </span>
            }
            className={`!mb-2 ${recurrencyType === 'once' ? '!opacity-70' : ''}`}
          />

          {isFreezingEnabled && recurrencyType !== 'once' && (
            <div className="space-y-3">
              {freezingPeriods.map((period, index) => (
                <div
                  key={period.id}
                  className="bg-white p-3 rounded-lg border border-gray-200 relative group"
                >
                  {index > 0 && (
                    <button
                      type="button"
                      onClick={() => removeFreezingPeriod(index)}
                      disabled={allDisabled}
                      className="absolute top-3 right-3 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition"
                      title="Remove Period"
                    >
                      <CloseIcon sx={{ fontSize: 16 }} />
                    </button>
                  )}
                  <div className="text-xs font-semibold text-gray-500 mb-3">
                    Period {index + 1}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 md:items-end gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <MuiDatePicker
                        label=""
                        value={
                          period.startDate
                            ? moment(period.startDate, 'YYYY-MM-DD')
                            : null
                        }
                        minDate={
                          getMinStartDate(index)
                            ? moment(getMinStartDate(index), 'YYYY-MM-DD')
                            : undefined
                        }
                        disabled={allDisabled}
                        inputFormat="MM/DD/YYYY"
                        onChange={(date) => {
                          updateFreezingPeriod(
                            index,
                            'startDate',
                            date && date.isValid()
                              ? date.format('YYYY-MM-DD')
                              : ''
                          );
                        }}
                        textFieldProps={{
                          size: 'small',
                          margin: 'none',
                          className: '!m-0',
                          placeholder: 'Start date',
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Start Time
                      </label>
                      {(() => {
                        const { hour: fsh, min: fsm } = normalizeTimeSelectParts(
                          period.startTime
                        );
                        return renderTimeInputs(
                          fsh,
                          (v) =>
                            updateFreezingPeriod(
                              index,
                              'startTime',
                              `${v}:${fsm}`
                            ),
                          fsm,
                          (v) =>
                            updateFreezingPeriod(
                              index,
                              'startTime',
                              `${fsh}:${v}`
                            ),
                          period.startAmPm,
                          (v) =>
                            updateFreezingPeriod(index, 'startAmPm', v)
                        );
                      })()}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Date (Max 30 days)
                      </label>
                      <MuiDatePicker
                        label=""
                        value={
                          period.endDate
                            ? moment(period.endDate, 'YYYY-MM-DD')
                            : null
                        }
                        minDate={
                          period.startDate
                            ? moment(period.startDate, 'YYYY-MM-DD')
                            : undefined
                        }
                        maxDate={
                          period.startDate &&
                          getMaxEndDate(period.startDate)
                            ? moment(
                                getMaxEndDate(period.startDate),
                                'YYYY-MM-DD'
                              )
                            : undefined
                        }
                        disabled={allDisabled || !period.startDate}
                        inputFormat="MM/DD/YYYY"
                        onChange={(date) => {
                          updateFreezingPeriod(
                            index,
                            'endDate',
                            date && date.isValid()
                              ? date.format('YYYY-MM-DD')
                              : ''
                          );
                        }}
                        textFieldProps={{
                          size: 'small',
                          margin: 'none',
                          className: '!m-0',
                          placeholder: 'End date',
                        }}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        End Time
                      </label>
                      {(() => {
                        const { hour: feh, min: fem } = normalizeTimeSelectParts(
                          period.endTime
                        );
                        return renderTimeInputs(
                          feh,
                          (v) =>
                            updateFreezingPeriod(
                              index,
                              'endTime',
                              `${v}:${fem}`
                            ),
                          fem,
                          (v) =>
                            updateFreezingPeriod(
                              index,
                              'endTime',
                              `${feh}:${v}`
                            ),
                          period.endAmPm,
                          (v) =>
                            updateFreezingPeriod(index, 'endAmPm', v)
                        );
                      })()}
                    </div>
                  </div>
                </div>
              ))}

              {freezingPeriods.length < 3 && (
                <button
                  type="button"
                  onClick={handleAddFreezingPeriod}
                  disabled={allDisabled}
                  className="w-full py-2 border border-dashed border-primary-300 text-primary-500 rounded-lg text-sm font-medium hover:bg-primary-50 transition disabled:opacity-50"
                >
                  + Add Another Period
                </button>
              )}

              {!isReadOnly && summary.zeroReason === 'freeze' && (
                <div className="mt-2 flex items-start gap-2 text-xs text-red-800 bg-[#F65C5C]/10 p-2.5 rounded border border-[#F65C5C]/30">
                  <ScheduleSectionErrorIcon className="mt-0.5" />
                  <p className="m-0 min-w-0 flex-1 leading-snug">{FREEZE_ZERO_EXECUTIONS_ERROR}</p>
                </div>
              )}

              {!isReadOnly && hasFreezingPeriodOverlap && (
                <div className="mt-2 flex items-start gap-2 text-xs text-red-800 bg-[#F65C5C]/10 p-2.5 rounded border border-[#F65C5C]/30">
                  <ScheduleSectionErrorIcon className="mt-0.5" />
                  <p className="m-0 min-w-0 flex-1 leading-snug">
                    <span className="font-semibold text-gray-900">Error:</span> {FREEZING_PERIODS_OVERLAP_ERROR}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Schedule Summary */}
        <div className="rounded-lg border border-gray-200 bg-slate-50/90 p-4">
          <div className="mb-4 flex items-center gap-2 text-base font-semibold text-gray-800">
            <CalendarTodayOutlinedIcon sx={{ fontSize: 22 }} className="text-primary-500" />
            Schedule Summary
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                First Test Execution Date
              </div>
              <div
                className={`text-base font-semibold ${
                  summary.first === '-' ? 'text-gray-400' : 'text-gray-900'
                }`}
              >
                {summary.first}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Last Test Execution Date
              </div>
              <div
                className={`text-base font-semibold ${
                  summary.last === '-' ? 'text-gray-400' : 'text-gray-900'
                }`}
              >
                {summary.last}
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
                Total no. Of Executions
              </div>
              <div
                className={`text-base font-semibold ${
                  summary.total === '-' || summary.total === '0'
                    ? 'text-gray-400'
                    : 'text-gray-900'
                }`}
              >
                {summary.total}
              </div>
            </div>
          </div>
        </div>

        <div className="!mt-4 border-t-2 border-[#EFEFEF]" />

        {/* Submit button */}
        {!isReadOnly && (
          <div className="flex justify-start gap-5 mt-6">
            <Button
              onClick={handleSubmit}
              disabled={isDisabled || disableSubmit}
              className={`flex items-center gap-2 px-4 py-3 ${
                isDisabled || disableSubmit
                  ? '!bg-gray-50 !text-gray-500 cursor-not-allowed'
                  : '!bg-primary-500 hover:!bg-primary-600 !text-white cursor-pointer'
              }`}
            >
              {submitButtonLabel}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default ScheduleTest;
